import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchMtrTemplateList } from '#/api/imsMgmt/settings/mtrTemplate/fetchList';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { DEFAULT_LIST_PAGE_SIZE } from '#/utils/listUrl/listPaginationDefaults';
import {
  buildListSearchParams,
  parseListSearchParams,
} from '#/utils/listUrl/parseListSearchParams';
import { mtrTemplateListQueryKey } from '#/utils/queryKeys';

const EMPTY_PAGE = {
  current_page: 1,
  page_size: DEFAULT_LIST_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

function useMtrTemplateList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const listQuery = useMemo(() => parseListSearchParams(searchParams), [searchParams]);

  const query = useQuery({
    queryKey: mtrTemplateListQueryKey(listQuery),
    queryFn: () => fetchMtrTemplateList(listQuery),
    placeholderData: keepPreviousData,
  });

  const setListQuery = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(listQuery) : updater;
    setSearchParams(buildListSearchParams(next), { replace: true });
  }, [listQuery, setSearchParams]);

  const setPage = useCallback((page) => {
    setListQuery((prev) => ({ ...prev, page }));
  }, [setListQuery]);

  const setPageSize = useCallback((pageSize) => {
    setListQuery((prev) => ({ ...prev, pageSize, page: 1 }));
  }, [setListQuery]);

  const setKeyword = useCallback((keyword) => {
    setListQuery((prev) => ({ ...prev, keyword, page: 1 }));
  }, [setListQuery]);

  const setFilter = useCallback((key, value) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, [setListQuery]);

  const setSort = useCallback((sort) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: sort ?? [],
    }));
  }, [setListQuery]);

  const removeSortAt = useCallback((index) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: (prev.sort ?? []).filter((_, sortIndex) => sortIndex !== index),
    }));
  }, [setListQuery]);

  const resetListQuery = useCallback(() => {
    setListQuery((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      keyword: '',
      sort: [],
      filters: {},
    }));
  }, [setListQuery]);

  return {
    rows: query.data?.data ?? [],
    pagination: query.data?.page ?? EMPTY_PAGE,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    refetch: query.refetch,
  };
}

export default useMtrTemplateList;
